import { BaseHolding, WebUserDataConstants } from '@ddt-web/domain';
import * as groupBy from 'lodash/groupBy';
import * as isNil from 'lodash/isNil';
import * as sumBy from 'lodash/sumBy';

export class HoldingsUtil {
	private static readonly SECURITY_ID_REGEX = /^[\w-]+@[\w-]+@[\w-]*(@\w+)?$/;
	private static readonly CASH_IDENTIFIER = 'CASH-USD@XUSD00000@';

	/***
	 * @description removeDuplicateHoldings returns a list of holdings with duplicates removed, while aggregating the weights of the duplicates.
	 *
	 * @param {any} holdings: keeping this as an any because the makeup of holdings can vary.
	 * @param {string} filter: filter used to group holdings
	 */
	public static removeDuplicateHoldings(holdings: any, filter: string): Array<BaseHolding> {
		const grouped = groupBy(holdings, filter);
		const aggregated = Object.entries(grouped).map(([identifier, holdingInfo]) => {
			const holdingBase = holdingInfo[0];
			const updatedHolding = {
				identifier,
				identifierType: holdingBase?.identifierType,
				weight: sumBy(holdingInfo, 'weight'),
				subType: holdingBase?.subType,
				morningstarSecId: holdingBase?.morningstarSecId,
				globalId: holdingBase?.globalId,
				aladdinId: holdingBase?.aladdinId || holdingBase?.assetId,
				cusip: holdingBase?.cusip,
				assetType: holdingBase?.assetType
			};

			return updatedHolding;
		});

		return aggregated;
	}

	/**
	 * Method used to generate identifier and identifierType. Used within US tools as some old securities would have old format (securityId).
	 * If identifierKeys are passed we skip the old format generation and set identifiers based on the identifierKeys
	 * @param holding 
	 * @returns identifier & identifierType
	 */
	public static generateIdentifierAndIdentifierType(holding: any, identifierKeys?: string[]): { identifier: string; identifierType: string } {

		if (isNil(identifierKeys)) {
			const securityId = this.buildSecurityId(holding);
			if (securityId) {
				return {
					identifier: securityId,
					identifierType: WebUserDataConstants.IDENTIFIER_TO_IDENTIFIER_TYPE_MAPPING['securityId']
				};
			}
		}

		identifierKeys = identifierKeys || Object.keys(WebUserDataConstants.IDENTIFIER_TO_IDENTIFIER_TYPE_MAPPING);
		const identifierKey = identifierKeys.find(identifierKey => !!holding?.[identifierKey]);
		if (identifierKey) {
			return {
				identifier: holding[identifierKey],
				identifierType: WebUserDataConstants.IDENTIFIER_TO_IDENTIFIER_TYPE_MAPPING[identifierKey]
			};
		}

		return {
			identifier: holding?.identifier,
			identifierType: holding?.identifierType
		};
	}

	/**
	 * This is temporary for WDS Decom. This is mainly to store securities in PE with old SECURITY_ID to support backward compatibility.
	 * TODO wdsDecom: Remove this later
	 */
	private static buildSecurityId(security: any) {
		if (!security) {
			return null;
		}

		if (security.identifierType === WebUserDataConstants.IDENTIFIER_TYPE.SECURITY_ID) {
			return security.identifier;
		}

		if (security.commonIdentifierType === WebUserDataConstants.IDENTIFIER_TYPE.SECURITY_ID) {
			return security.commonIdentifier;
		}

		// This is mainly for ERA case where the identifier passed in is already security ID
		if (this.SECURITY_ID_REGEX.test(security.identifier)) {
			return security.identifier;
		}

		const ticker = security.ticker;
		const cusip = security.publicCusip || security.cusip || security.aladdinId;
		const isin = security.isin;
		const sedol = security.sedol;

		const isCash = ticker?.includes('CASH') || cusip?.includes('XUSD');
		if (isCash) {
			return HoldingsUtil.CASH_IDENTIFIER;
		}

		if (security.assetType === 'ASSET_TYPE_MODEL' && ticker && cusip) {
			// Mainly for alts
			return [ticker, cusip, ''].join('@');
		}

		const containsAllIdentifiers = ticker && cusip && isin;
		if (containsAllIdentifiers) {
			const isGBSecurity = security.region === 'GB' || security.regionCode === 'GB';
			if (isGBSecurity && sedol) {
				return [ticker, cusip, isin, sedol].join('@');
			}
			return [ticker, cusip, isin].join('@');
		}
		return null;
	}

	public static isCashIdentifier(identifier: string): boolean {
		return identifier === HoldingsUtil.CASH_IDENTIFIER;
	}

	// commonIdentifierType only exists on engagement positions and WWE associated portfolio positions which store the secId as the commonIdentifier
	// don't use this exclusively to check for apb positions
	public static shouldUseCommonIdentifierForHolding(security: BaseHolding): boolean {
		return security.commonIdentifierType === WebUserDataConstants.IDENTIFIER_TYPE.SECURITY_ID;
	}

	/**
	 * Returns the identifiers that are required for every security
	 * excluding identifier and identifierType
	 */
	public static retrieveImportantSecurityIdentifiers(security: any, extraIdentifiers: string[] = []) {
		const updatedSecurity = {};
		const getSecurityIdentifierValue = identifier => {
			return security?.[identifier]
				?? security?.allData?.[identifier]
				?? security?.identifiers?.[identifier]
				?? security?.funds?.[identifier]?.stringValue

				// Sometimes its all inside asset
				?? security?.asset?.[identifier]
				?? security?.asset?.allData?.[identifier]
				?? security?.asset?.identifiers?.[identifier]
				?? security?.asset?.funds?.[identifier]?.stringValue;
		};

		const combinedIdentifiers = [...WebUserDataConstants.SECURITY_IMPORTANT_FIELDS, ...extraIdentifiers];
		for (const identifier of combinedIdentifiers) {
			let value = getSecurityIdentifierValue(identifier);

			if (identifier === 'aladdinId') {
				value = getSecurityIdentifierValue('assetId') ?? value;
			}

			if (value) {
				updatedSecurity[identifier] = value;
			}
		}
		return updatedSecurity;
	}
}
